import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, Building2Icon, DownloadIcon, FileSpreadsheetIcon, MapPinIcon, PhoneIcon, MailIcon, GlobeIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Company } from '../../types';
import { exportCompaniesToExcel } from '../../utils/excelExport';
export function Companies() {
  const {
    companies,
    addCompany,
    updateCompany,
    deleteCompany
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: '',
    location: '',
    phone: '',
    email: '',
    website: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredCompanies = companies.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.industry.toLowerCase().includes(searchTerm.toLowerCase()));
  const openNewForm = () => {
    setSelectedCompany(null);
    setFormData({
      name: '',
      industry: '',
      size: '',
      location: '',
      phone: '',
      email: '',
      website: '',
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      industry: company.industry,
      size: company.size,
      location: company.location,
      phone: company.phone,
      email: company.email,
      website: company.website || '',
      notes: company.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (company: Company) => {
    setSelectedCompany(company);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.industry) errors.industry = 'La industria es requerida';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedCompany) {
      updateCompany(selectedCompany.id, formData);
    } else {
      addCompany(formData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (selectedCompany) {
      deleteCompany(selectedCompany.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportCompaniesToExcel(companies);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-442">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-443">
        <div data-id="element-444">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-445">
            Empresas
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-446">
            Gestión de empresas y organizaciones
          </p>
        </div>
        {canCreate('companies') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-448" />} onClick={openNewForm} data-id="element-447">
            Nueva Empresa
          </Button>}
      </div>

      {/* Export Card */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800" data-id="element-449">
        <div className="flex items-center justify-between" data-id="element-450">
          <div className="flex items-center gap-4" data-id="element-451">
            <div className="w-14 h-14 rounded-xl bg-blue-500 dark:bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20" data-id="element-452">
              <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-453" />
            </div>
            <div data-id="element-454">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-455">
                Exportar Base de Datos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-456">
                Descarga todas las empresas en formato Excel
              </p>
              <div className="flex items-center gap-4 mt-2" data-id="element-457">
                <div className="flex items-center gap-1.5 text-sm text-blue-700 dark:text-blue-400" data-id="element-458">
                  <Building2Icon className="w-4 h-4" data-id="element-459" />
                  <span className="font-medium" data-id="element-460">
                    {companies.length} empresas
                  </span>
                </div>
              </div>
            </div>
          </div>
          <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-462" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-200" data-id="element-461">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </Card>

      {/* Filters */}
      <Card data-id="element-463">
        <div className="relative" data-id="element-464">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-465" />
          <Input placeholder="Buscar empresas por nombre o industria..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-466" />
        </div>
      </Card>

      {/* Companies List */}
      <Card padding="none" data-id="element-467">
        <div className="overflow-x-auto" data-id="element-468">
          <table className="w-full" data-id="element-469">
            <thead data-id="element-470">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-471">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-472">
                  Empresa
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-473">
                  Contacto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-474">
                  Ubicación
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-475">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-476">
              {filteredCompanies.length === 0 ? <tr data-id="element-477">
                  <td colSpan={4} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-478">
                    No se encontraron empresas
                  </td>
                </tr> : filteredCompanies.map(company => <tr key={company.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-479">
                    <td className="py-4 px-6" data-id="element-480">
                      <div className="flex items-center gap-3" data-id="element-481">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400" data-id="element-482">
                          <Building2Icon className="w-5 h-5" data-id="element-483" />
                        </div>
                        <div data-id="element-484">
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-485">
                            {company.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-486">
                            {company.industry} • {company.size}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-487">
                      <div className="space-y-1" data-id="element-488">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-489">
                          <PhoneIcon className="w-3.5 h-3.5" data-id="element-490" />
                          {company.phone}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-491">
                          <MailIcon className="w-3.5 h-3.5" data-id="element-492" />
                          {company.email}
                        </div>
                        {company.website && <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400" data-id="element-493">
                            <GlobeIcon className="w-3.5 h-3.5" data-id="element-494" />
                            <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline" data-id="element-495">
                              {company.website}
                            </a>
                          </div>}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-496">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-497">
                        <MapPinIcon className="w-4 h-4" data-id="element-498" />
                        {company.location}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-499">
                      <div className="flex items-center justify-end gap-2" data-id="element-500">
                        {canEdit('companies') && <button onClick={() => openEditForm(company)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-501">
                            <EditIcon className="w-4 h-4" data-id="element-502" />
                          </button>}
                        {canDelete('companies') && <button onClick={() => openDeleteConfirm(company)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-503">
                            <TrashIcon className="w-4 h-4" data-id="element-504" />
                          </button>}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedCompany ? 'Editar Empresa' : 'Nueva Empresa'} size="lg" data-id="element-505">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-506">
          <Input label="Nombre de la Empresa" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} error={formErrors.name} data-id="element-507" />

          <div className="grid grid-cols-2 gap-4" data-id="element-508">
            <Input label="Industria" value={formData.industry} onChange={e => setFormData({
            ...formData,
            industry: e.target.value
          })} error={formErrors.industry} data-id="element-509" />
            <Input label="Tamaño (Empleados)" value={formData.size} onChange={e => setFormData({
            ...formData,
            size: e.target.value
          })} placeholder="Ej: 50-100" data-id="element-510" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-511">
            <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} data-id="element-512" />
            <Input label="Email" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} data-id="element-513" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-514">
            <Input label="Ubicación" value={formData.location} onChange={e => setFormData({
            ...formData,
            location: e.target.value
          })} data-id="element-515" />
            <Input label="Sitio Web" value={formData.website} onChange={e => setFormData({
            ...formData,
            website: e.target.value
          })} placeholder="www.ejemplo.com" data-id="element-516" />
          </div>

          <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
          ...formData,
          notes: e.target.value
        })} rows={3} data-id="element-517" />

          <div className="flex justify-end gap-3 pt-4" data-id="element-518">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-519">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-520">
              {selectedCompany ? 'Guardar Cambios' : 'Crear Empresa'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-521">
        <div className="space-y-4" data-id="element-522">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-523">
            ¿Está seguro de que desea eliminar la empresa "
            {selectedCompany?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-524">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-525">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-526">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}